import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import StroopTask from '../components/cognitive/StroopTask';
import TrailMakingTask from '../components/cognitive/TrailMakingTask';
import DigitSpanTask from '../components/cognitive/DigitSpanTask';
import { ArrowLeft, Zap, Target, Brain, ChevronRight } from 'lucide-react';

const TASKS = [
  {
    id: 'stroop',
    name: 'Stroop Görevi',
    subtitle: 'Dikkat ve İnhibisyon',
    description: 'Renk-kelime çelişkisini kullanarak seçici dikkat ve işlemleme hızını ölçer.',
    icon: Zap,
    color: 'purple',
    time: '~2 dk',
  },
  {
    id: 'trail_making',
    name: 'Trail Making',
    subtitle: 'Görsel Tarama ve Esneklik',
    description: 'Sayı ve harf bağlama göreviyle görsel tarama hızını ve bilişsel esnekliği ölçer.',
    icon: Target,
    color: 'teal',
    time: '~3 dk',
  },
  {
    id: 'digit_span',
    name: 'Sayı Dizisi',
    subtitle: 'Bellek Kapasitesi',
    description: 'İleri ve geri sayı dizisi tekrarıyla kısa süreli ve çalışma belleğini ölçer.',
    icon: Brain,
    color: 'amber',
    time: '~3 dk',
  },
];

const colorStyles = {
  purple: { bg: 'bg-purple-500/15', border: 'border-purple-500/30', text: 'text-purple-400', iconBg: 'bg-purple-500/20' },
  teal: { bg: 'bg-teal-500/15', border: 'border-teal-500/30', text: 'text-teal-400', iconBg: 'bg-teal-500/20' },
  amber: { bg: 'bg-amber-500/15', border: 'border-amber-500/30', text: 'text-amber-400', iconBg: 'bg-amber-500/20' },
};

export default function CognitiveTaskPage() {
  const navigate = useNavigate();
  const { taskId } = useParams();
  const [activeTask, setActiveTask] = useState(taskId || null);

  const handleBack = () => {
    if (activeTask) {
      setActiveTask(null);
      navigate('/cognitive', { replace: true });
    } else {
      navigate('/dashboard');
    }
  };

  // Render active task
  if (activeTask === 'stroop') return <StroopTask onBack={handleBack} />;
  if (activeTask === 'trail_making') return <TrailMakingTask onBack={handleBack} />;
  if (activeTask === 'digit_span') return <DigitSpanTask onBack={handleBack} />;

  // Task Hub
  return (
    <div className="min-h-screen bg-transparent py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-white/50 hover:text-white/80 mb-6 transition" data-testid="cognitive-back">
          <ArrowLeft className="w-4 h-4" /> Dashboard'a Dön
        </button>

        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white mb-2" data-testid="cognitive-title">Bilişsel Görevler</h1>
          <p className="text-white/50 text-sm leading-relaxed">
            Aşağıdaki interaktif görevler dikkat, bellek, işlemleme hızı ve bilişsel esneklik alanlarını değerlendirir.
            Sonuçlar AI Doktor'a aktarılarak genel bilişsel profilinize katkıda bulunur.
          </p>
        </div>

        <div className="space-y-4" data-testid="cognitive-task-list">
          {TASKS.map(task => {
            const Icon = task.icon;
            const style = colorStyles[task.color];
            return (
              <button key={task.id}
                onClick={() => { setActiveTask(task.id); navigate(`/cognitive/${task.id}`, { replace: true }); }}
                className={`w-full ${style.bg} border ${style.border} rounded-2xl p-5 text-left transition-all hover:scale-[1.01] active:scale-[0.99] group`}
                data-testid={`cognitive-task-${task.id}`}>
                <div className="flex items-center gap-4">
                  <div className={`${style.iconBg} w-12 h-12 rounded-xl flex items-center justify-center shrink-0`}>
                    <Icon className={`w-6 h-6 ${style.text}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <h3 className="text-white font-semibold text-base">{task.name}</h3>
                      <span className="text-white/30 text-xs">{task.time}</span>
                    </div>
                    <p className={`${style.text} text-xs font-medium mb-1`}>{task.subtitle}</p>
                    <p className="text-white/40 text-xs leading-relaxed">{task.description}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-white/20 group-hover:text-white/40 transition shrink-0" />
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-6 bg-white/5 border border-white/10 rounded-xl p-4">
          <p className="text-white/30 text-xs leading-relaxed">
            Bu görevler tanı aracı değildir. Bilişsel performansınızı takip etmek ve zaman içindeki değişimleri gözlemlemek amacıyla kullanılır.
            Endişe verici sonuçlar için bir sağlık profesyoneline danışmanızı öneririz.
          </p>
        </div>
      </div>
    </div>
  );
}
